import React from 'react';
import { Edit, Power, PowerOff, AlertCircle, ArrowUp, ArrowDown } from 'lucide-react';
import { Button } from '@/components/ui/button';
import { Badge } from '@/components/ui/badge';
import { Pagination } from '@/components/ui/pagination';
import { SortableHeader } from '@/components/ui/sortable-header';
import { useTableData } from '@/hooks/useTableData';
import type { MovementType } from '../types/system-parameters.types';

interface MovementTypesTableProps {
  movementTypes: MovementType[];
  onEdit: (movementType: MovementType) => void;
  onDelete: (movementType: MovementType) => void;
}

type SortField = 'name' | 'code' | 'category' | 'orderIndex';

export const MovementTypesTable: React.FC<MovementTypesTableProps> = ({
  movementTypes,
  onEdit,
  onDelete
}) => {
  const fieldLabels: Record<SortField, string> = {
    name: 'Tipo',
    code: 'Código',
    category: 'Categoría',
    orderIndex: 'Orden'
  };

  const sortValueExtractor = (movementType: MovementType, field: SortField): any => {
    switch (field) {
      case 'name':
        return movementType.additionalData.name.toLowerCase();
      case 'code':
        return movementType.additionalData.code.toLowerCase();
      case 'category':
        return movementType.additionalData.category.toLowerCase();
      case 'orderIndex':
        return movementType.additionalData.orderIndex;
      default:
        return movementType.additionalData.name;
    }
  };

  const tableData = useTableData<MovementType, SortField>(
    {
      data: movementTypes,
      defaultSortField: 'orderIndex',
      defaultSortDirection: 'asc',
      itemsPerPage: 10,
      sortValueExtractor
    },
    fieldLabels
  );

  if (movementTypes.length === 0) {
    return (
      <div className="text-center py-12">
        <h3 className="text-lg font-medium text-gray-900 mb-2">No hay tipos de movimiento</h3>
        <p className="text-gray-500">No se encontraron tipos de movimiento con los filtros aplicados.</p>
      </div>
    );
  }

  return (
    <div>
      <div className="overflow-x-auto">
        <table className="w-full">
          <thead className="bg-gray-50 border-b">
            <tr>
              <SortableHeader
                field="name"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Tipo
              </SortableHeader>
              <SortableHeader
                field="code"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Código
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Dirección
              </th>
              <SortableHeader
                field="category"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-left"
              >
                Categoría
              </SortableHeader>
              <SortableHeader
                field="orderIndex"
                onSort={tableData.handleSort}
                getSortIcon={tableData.getSortIcon}
                className="text-center"
              >
                Orden
              </SortableHeader>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Estado
              </th>
              <th className="px-4 py-3 text-center text-xs font-medium text-gray-500 uppercase tracking-wider">
                Acciones
              </th>
            </tr>
          </thead>
          <tbody className="bg-white divide-y divide-gray-200">
            {tableData.pagination.paginatedData.map((movementType) => (
              <tr key={movementType.id} className="hover:bg-gray-50">
                <td className="px-4 py-4">
                  <div>
                    <div className="text-sm font-medium text-gray-900">
                      {movementType.additionalData.name}
                    </div>
                    {movementType.additionalData.requiresApproval && (
                      <div className="flex items-center gap-1 text-xs text-orange-600 mt-1">
                        <AlertCircle className="h-3 w-3" />
                        Requiere aprobación
                      </div>
                    )}
                    {movementType.additionalData.description && (
                      <div className="text-xs text-gray-500 mt-1">
                        {movementType.additionalData.description}
                      </div>
                    )}
                    <div className="text-xs text-gray-400 mt-1">
                      ID: {movementType.id}
                    </div>
                  </div>
                </td>

                <td className="px-4 py-4">
                  <div className="text-sm font-mono text-gray-600">
                    {movementType.additionalData.code}
                  </div>
                </td>

                <td className="px-4 py-4 text-center">
                  <Badge
                    variant={movementType.additionalData.direction === 'entry' ? 'default' : 'destructive'}
                    className="flex items-center gap-1 w-fit mx-auto"
                  >
                    {movementType.additionalData.direction === 'entry' ? (
                      <>
                        <ArrowDown className="h-3 w-3" />
                        Entrada
                      </>
                    ) : (
                      <>
                        <ArrowUp className="h-3 w-3" />
                        Salida
                      </>
                    )}
                  </Badge>
                </td>

                <td className="px-4 py-4">
                  <div className="text-sm text-gray-600">
                    {movementType.additionalData.category}
                  </div>
                </td>

                <td className="px-4 py-4 text-center">
                  <div className="text-sm font-medium text-gray-900">
                    {movementType.additionalData.orderIndex}
                  </div>
                </td>

                <td className="px-4 py-4 text-center">
                  <Badge className={movementType.additionalData.isActive ? 'bg-green-100 text-green-700' : 'bg-gray-100 text-gray-700'}>
                    {movementType.additionalData.isActive ? 'Activo' : 'Inactivo'}
                  </Badge>
                </td>

                <td className="px-4 py-4 text-center">
                  <div className="flex gap-2 justify-center">
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onEdit(movementType)}
                      className="text-xs"
                    >
                      <Edit className="w-3 h-3 mr-1" />
                      Editar
                    </Button>
                    <Button
                      variant="outline"
                      size="sm"
                      onClick={() => onDelete(movementType)}
                      className={`text-xs ${movementType.additionalData.isActive
                        ? 'text-orange-600 hover:text-orange-700 hover:bg-orange-50'
                        : 'text-green-600 hover:text-green-700 hover:bg-green-50'}`}
                    >
                      {movementType.additionalData.isActive ? (
                        <>
                          <PowerOff className="w-3 h-3 mr-1" />
                          Desactivar
                        </>
                      ) : (
                        <>
                          <Power className="w-3 h-3 mr-1" />
                          Reactivar
                        </>
                      )}
                    </Button>
                  </div>
                </td>
              </tr>
            ))}
          </tbody>
        </table>
      </div>

      {/* Sort info */}
      <div className="mt-2 text-sm text-gray-500">
        Ordenado por: <span className="font-medium">
          {tableData.sortInfo.label}
        </span> ({tableData.sortInfo.direction === 'asc' ? 'Ascendente' : 'Descendente'})
      </div>

      {/* Pagination */}
      <Pagination
        currentPage={tableData.pagination.currentPage}
        totalPages={tableData.pagination.totalPages}
        onPageChange={tableData.pagination.goToPage}
        showingFrom={tableData.pagination.showingFrom}
        showingTo={tableData.pagination.showingTo}
        totalItems={tableData.pagination.totalItems}
        itemsPerPage={tableData.pagination.itemsPerPage}
        onItemsPerPageChange={tableData.pagination.setItemsPerPage}
      />
    </div>
  );
};
